/**
 * ControllateTableInput — Tabella strutturata 5 colonne per
 * "Reale Mutua 2.1 — Società controllate o partecipate".
 *
 * Design (Fase 4.9):
 *  - Desktop (≥md): layout tabellare orizzontale, 5 colonne, riga compatta,
 *    bottone rimuovi piccolo all'estrema destra.
 *  - Mobile (<md): card per riga con titolo "Società controllata #N",
 *    label sopra ogni input, bottone rimuovi full-width in fondo.
 *  - Max 5 righe (allineato al template PDF Reale Mutua tabella 2.1).
 *  - Non fa padding automatico: l'utente decide quante righe.
 *
 * Props:
 *  - value    : Array<ControllataItem>  (default [])
 *  - onChange : (Array<ControllataItem>) => void
 *  - maxRows  : number (default 5)
 */
import React, { useMemo } from "react";
import { Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const COL_KEYS = ["nome", "partecipazione_pct", "nazione_sede",
                   "totale_attivo_keur", "attivita_svolta"];

// Mappa key → suffisso data-testid conforme alla specifica utente
// (`nome`, `pct`, `nazione`, `attivo`, `attivita`).
const TID_SUFFIX = {
  nome: "nome",
  partecipazione_pct: "pct",
  nazione_sede: "nazione",
  totale_attivo_keur: "attivo",
  attivita_svolta: "attivita",
};

const COL_META = {
  nome:               { label: "Società",              type: "text",    maxLen: 60,  placeholder: "Denominazione" },
  partecipazione_pct: { label: "Partecipazione %",     type: "number",  min: 0, max: 100, step: "0.01", placeholder: "%" },
  nazione_sede:       { label: "Nazione sede",          type: "text",    maxLen: 40,  placeholder: "Es. Italia", list: "controllate-nazioni-comuni" },
  totale_attivo_keur: { label: "Totale Attivo (K€)",   type: "number",  min: 0, step: "1", placeholder: "0", helper: "in migliaia di euro" },
  attivita_svolta:    { label: "Attività svolta",       type: "text",    maxLen: 200, placeholder: "Es. Consulenza" },
};

function emptyRow() {
  return { nome: "", partecipazione_pct: "", nazione_sede: "",
           totale_attivo_keur: "", attivita_svolta: "" };
}

/**
 * Best-effort parser da stringa legacy "Nome - % - Nazione - K€ - Attività"
 * (uno per riga). Usato per pre-popolare l'array quando arriva un value
 * legacy in formato string (bozze salvate con la vecchia textarea).
 */
export function parseLegacyControllateString(txt) {
  if (!txt || typeof txt !== "string") return [];
  const lines = txt.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const parts = line.split(/\s*[-–—;|\t]\s*/);
    const row = emptyRow();
    if (parts[0]) row.nome = parts[0];
    if (parts[1]) {
      const m = parts[1].match(/([\d.,]+)/);
      if (m) row.partecipazione_pct = m[1].replace(",", ".");
    }
    if (parts[2]) row.nazione_sede = parts[2];
    if (parts[3]) {
      const m = parts[3].match(/([\d.,]+)/);
      if (m) row.totale_attivo_keur = m[1].replace(/[.\s]/g, "").replace(",", ".");
    }
    if (parts[4]) row.attivita_svolta = parts[4];
    rows.push(row);
  }
  return rows;
}

export function ControllateTableInput({ value, onChange, maxRows = 5 }) {
  // Normalizza value: array | legacy string | null → array
  const rows = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim().length > 0) {
      return parseLegacyControllateString(value);
    }
    return [];
  }, [value]);

  // Vincolo: mostra sempre almeno 1 riga vuota per invitare la compilazione.
  const displayRows = rows.length === 0 ? [emptyRow()] : rows;
  const displayIsSynthetic = rows.length === 0;

  const emit = (next) => {
    // Compatta: rimuove righe null/undefined ma NON righe con soli campi vuoti
    // (l'utente potrebbe stare digitando).
    const cleaned = next.filter((r) => r !== null && r !== undefined);
    onChange(cleaned);
  };

  const updateRow = (idx, colKey, newVal) => {
    // Se stiamo modificando la riga sintetica (rows era vuoto), materializzala
    const base = displayIsSynthetic ? [emptyRow()] : rows.map((r) => ({ ...r }));
    while (base.length <= idx) base.push(emptyRow());
    base[idx] = { ...base[idx], [colKey]: newVal };
    emit(base);
  };

  const addRow = () => {
    if (rows.length >= maxRows) return;
    const base = displayIsSynthetic ? [emptyRow()] : rows.map((r) => ({ ...r }));
    emit([...base, emptyRow()]);
  };

  const removeRow = (idx) => {
    const base = rows.map((r) => ({ ...r }));
    base.splice(idx, 1);
    emit(base);
  };

  return (
    <div className="rounded-2xl border border-[#E5E2DB] bg-white p-3 sm:p-4"
         data-testid="controllate-table">
      {/* ---------------- Desktop table ---------------- */}
      <div className="hidden md:block">
        <div className="grid grid-cols-[minmax(180px,2.2fr)_110px_140px_150px_minmax(180px,2fr)_32px]
                         gap-2 px-1 pb-2 text-[11px] uppercase tracking-wide
                         text-[#4A5E51] font-medium border-b border-[#E5E2DB]">
          {COL_KEYS.map((k) => (
            <div key={k}>{COL_META[k].label}</div>
          ))}
          <div /> {/* colonna azione */}
        </div>
        <div>
          {displayRows.map((row, idx) => (
            <div key={idx}
                 className="grid grid-cols-[minmax(180px,2.2fr)_110px_140px_150px_minmax(180px,2fr)_32px]
                             gap-2 items-center py-2 border-b border-[#F0EDE7]
                             last:border-b-0"
                 data-testid={`controllate-row-${idx}`}>
              {COL_KEYS.map((k) => {
                const meta = COL_META[k];
                return (
                  <Input key={k}
                          type={meta.type}
                          inputMode={meta.type === "number" ? "decimal" : undefined}
                          min={meta.min}
                          max={meta.max}
                          step={meta.step}
                          maxLength={meta.maxLen}
                          list={meta.list}
                          value={row?.[k] ?? ""}
                          onChange={(e) => updateRow(idx, k, e.target.value)}
                          placeholder={meta.placeholder}
                          data-testid={`controllate-row-${idx}-${TID_SUFFIX[k]}`}
                          className="rounded-lg border-[#E5E2DB] h-9 text-sm" />
                );
              })}
              <button type="button"
                      onClick={() => removeRow(idx)}
                      disabled={displayIsSynthetic}
                      data-testid={`controllate-remove-row-${idx}`}
                      aria-label={`Rimuovi riga ${idx + 1}`}
                      className="inline-flex items-center justify-center w-8 h-8
                                  rounded-md text-[#7A1D1D] hover:bg-[#7A1D1D]/10
                                  disabled:opacity-30 disabled:cursor-not-allowed
                                  transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ---------------- Mobile cards ---------------- */}
      <div className="md:hidden space-y-4">
        {displayRows.map((row, idx) => (
          <div key={idx}
               className="rounded-xl border border-[#E5E2DB] bg-[#FDFCFA] p-3"
               data-testid={`controllate-row-${idx}`}>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#E5E2DB]">
              <span className="text-sm font-medium text-[#163321]">
                Società controllata #{idx + 1}
              </span>
            </div>
            <div className="space-y-3">
              {COL_KEYS.map((k) => {
                const meta = COL_META[k];
                return (
                  <div key={k}>
                    <Label className="block text-xs text-[#4A5E51] mb-1">
                      {meta.label}
                    </Label>
                    <Input type={meta.type}
                            inputMode={meta.type === "number" ? "decimal" : undefined}
                            min={meta.min}
                            max={meta.max}
                            step={meta.step}
                            maxLength={meta.maxLen}
                            list={meta.list}
                            value={row?.[k] ?? ""}
                            onChange={(e) => updateRow(idx, k, e.target.value)}
                            placeholder={meta.placeholder}
                            data-testid={`controllate-row-${idx}-${TID_SUFFIX[k]}`}
                            className="rounded-lg border-[#E5E2DB] h-10 text-sm" />
                    {meta.helper && (
                      <p className="text-[10px] text-[#7A8E83] mt-0.5 italic">
                        {meta.helper}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <button type="button"
                    onClick={() => removeRow(idx)}
                    disabled={displayIsSynthetic}
                    data-testid={`controllate-remove-row-${idx}-mobile`}
                    className="w-full mt-3 inline-flex items-center justify-center
                                gap-1.5 py-2 rounded-lg text-sm text-[#7A1D1D]
                                border border-[#7A1D1D]/30 hover:bg-[#7A1D1D]/5
                                disabled:opacity-30 disabled:cursor-not-allowed
                                transition-colors">
              <Trash2 className="w-4 h-4" />
              Rimuovi società
            </button>
          </div>
        ))}
      </div>

      {/* datalist paesi comuni (condiviso desktop + mobile) */}
      <datalist id="controllate-nazioni-comuni">
        {["Italia", "Francia", "Germania", "Spagna", "Regno Unito", "Svizzera",
          "Austria", "Paesi Bassi", "Belgio", "Lussemburgo", "Portogallo",
          "Irlanda", "Polonia", "Romania", "Repubblica Ceca", "Ungheria",
          "USA", "Canada", "Brasile", "Argentina", "Cina", "Giappone",
          "India", "Australia"].map((n) => <option key={n} value={n} />)}
      </datalist>

      {/* ---------------- Footer add row ---------------- */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E5E2DB]">
        <span className="text-xs text-[#4A5E51]"
              data-testid="controllate-count">
          {rows.length}/{maxRows} righe
        </span>
        <button type="button"
                onClick={addRow}
                disabled={rows.length >= maxRows}
                data-testid="controllate-add-row-btn"
                className="inline-flex items-center gap-1.5 text-sm text-white
                            bg-[#163321] hover:bg-[#0F1F14]
                            disabled:bg-[#7A8E83] disabled:cursor-not-allowed
                            transition-colors px-3.5 py-2 rounded-full font-medium">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Aggiungi riga</span>
          <span className="sm:hidden">Aggiungi società controllata</span>
        </button>
      </div>
    </div>
  );
}

export default ControllateTableInput;
